import React, { useState, useContext } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import EDButton from '../../../../../../../../../../BaseComponents/Buttons/Button/EDButton';
import EDIcon from '../../../../../../../../../../BaseComponents/Icon/EDIcon';
import ChangeAnalysFavoriteMutation from './Queries/ChangeAnalysFavoriteMutation/ChangeAnalysFavoriteMutation';
import EkonomiskAnalysFavoriteAnalysDialog, {
  USE_NEW_AS_START_ANALYS_ID,
} from './EkonomiskAnalysFavoriteAnalysDialog';
import ChangeStartAnalysMutation from './Queries/ChangeStartAnalysMutation/ChangeStartAnalysMutation';
import { EkonomiskAnalysSideMenuContext } from '../../../Context/EkonomiskAnalysSideMenuContext';
import KombKodbehVisibilityQuery from './EkonomiskAnalysKombKodbehVisibility.gql';
import SetKombKodbehVisibilityMutation from './EkonomiskAnalysSetKombKodbehVisibility.gql';
import {
  EkonomiskAnalysSetKombKodbehVisibility,
  EkonomiskAnalysSetKombKodbehVisibilityVariables,
} from './_QueryTypes/EkonomiskAnalysSetKombKodbehVisibility';
import { EkonomiskAnalysContext } from '../../../../Context/EkonomiskAnalysContext';
import { IKombKodbehVisibility } from './Components/EkonomiskAnalysFavoriteOptionsMenu';
import EkonomiskAnalysListQuery from '../../../../../Queries/EkonomiskAnalysListQuery/AnalysListQuery.gql';
import {
  EkonomiskAnalysKombKodbehVisibilityQuery,
  EkonomiskAnalysKombKodbehVisibilityQueryVariables,
} from './_QueryTypes/EkonomiskAnalysKombKodbehVisibilityQuery';
import { EkonomiskAnalysSelectContext } from '../../../Components/AnalysSelect/Context/EkonomiskAnalysSelectContext';
import EkonomiskAnalysSaveDBSettingMutation from '../../../../../Queries/EkonomiskAnalysSaveDBSettingMutation/EkonomiskAnalysSaveDBSettingMutation';
import { ActiveDatabaseContext } from '../../../../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';

export interface IFavoriteAnalysDialogButtonProps {
  codeSystemId: string;
}

const EkonomiskAnalysFavoriteAnalysDialogButton: React.FunctionComponent<
  React.PropsWithChildren<IFavoriteAnalysDialogButtonProps>
> = props => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reloadingAnalysList, setReloadingAnalysList] = useState(false);
  const { fullAnalysList, selectAnalys } = useContext(
    EkonomiskAnalysSelectContext
  );
  const { userInfo } = useContext(EkonomiskAnalysSideMenuContext);
  const { dbName, codeSystemId } = useContext(EkonomiskAnalysContext);
  const openDialog = () => {
    setDialogOpen(true);
  };
  const { activeDatabase } = useContext(ActiveDatabaseContext);

  const queryResult = useQuery<
    EkonomiskAnalysKombKodbehVisibilityQuery,
    EkonomiskAnalysKombKodbehVisibilityQueryVariables
  >(KombKodbehVisibilityQuery, { variables: { dbname: dbName } });

  const [setKombKodbehVisibility] = useMutation<
    EkonomiskAnalysSetKombKodbehVisibility,
    EkonomiskAnalysSetKombKodbehVisibilityVariables
  >(SetKombKodbehVisibilityMutation, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: KombKodbehVisibilityQuery,
        variables: { dbname: dbName },
      },
      {
        query: EkonomiskAnalysListQuery,
        variables: {
          codeSystemId,
          onlyFavorites: false,
          dbName: activeDatabase ?? '',
        },
      },
    ],
  });

  const apolloClient = useApolloClient();

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const hideKombRes =
    queryResult.data?.ekanalyskombkodbehvisibility?.hide_komb_resultat ?? false;
  const hideKombBal =
    queryResult.data?.ekanalyskombkodbehvisibility?.hide_komb_balans ?? false;
  const hideKombBlandat =
    queryResult.data?.ekanalyskombkodbehvisibility?.hide_komb_blandat ?? false;
  const hideIngRes =
    queryResult.data?.ekanalyskombkodbehvisibility?.hide_ing_resultat ?? false;
  const hideIngBal =
    queryResult.data?.ekanalyskombkodbehvisibility?.hide_ing_balans ?? false;
  const hideIngBlandat =
    queryResult.data?.ekanalyskombkodbehvisibility?.hide_ing_blandat ?? false;

  return (
    <ChangeAnalysFavoriteMutation>
      {changeFavorite => (
        <ChangeStartAnalysMutation fullAnalysList={fullAnalysList}>
          {changeStartAnalys => (
            <EkonomiskAnalysSaveDBSettingMutation>
              {saveDBSetting => (
                <>
                  <EDButton
                    labelNotUpperCase
                    label="Redigera analysmeny"
                    icon={
                      <EDIcon iconType="edit" color="inherit" marginRight={1} />
                    }
                    onClick={openDialog}
                    flat
                    primary
                    padding={1}
                    lowEmphasis
                  />
                  <EkonomiskAnalysFavoriteAnalysDialog
                    changeFavorite={changeFavorite}
                    changeStartAnalys={info =>
                      changeStartAnalys(
                        info,
                        (analys, codePrivilege, unsavedAnalys, callback) => {
                          if (analys != null && codePrivilege != null) {
                            selectAnalys(
                              analys,
                              codePrivilege,
                              unsavedAnalys,
                              callback
                            );
                          }
                        }
                      )
                    }
                    open={dialogOpen}
                    onClose={closeDialog}
                    availableAnalyser={fullAnalysList}
                    startAnalys={userInfo ? userInfo.startAnalys : undefined}
                    groupingMode={
                      userInfo && userInfo.analysGroupingMode
                        ? userInfo.analysGroupingMode
                        : { id: '', value: 'kodbeh' }
                    }
                    saveDBSetting={saveDBSetting}
                    kombKodbehVisibility={{
                      hideKombRes,
                      hideKombBal,
                      hideIngBal,
                      hideIngRes,
                      hideIngBlandat,
                      hideKombBlandat,
                      useKombKodbeh:
                        queryResult.data?.ekanalyskombkodbehvisibility
                          ?.use_komb_kodbeh ?? false,
                    }}
                    reloadingAnalysList={reloadingAnalysList}
                    setKombKodbehVisibilityMutation={(
                      visibility: IKombKodbehVisibility
                    ) =>
                      setKombKodbehVisibility({
                        variables: {
                          hideIngBalans: visibility.hideIngBal,
                          hideIngResultat: visibility.hideIngRes,
                          hideKombBalans: visibility.hideKombBal,
                          hideKombResultat: visibility.hideKombRes,
                          hideKombBlandat: visibility.hideKombBlandat,
                          hideIngBlandat: visibility.hideIngBlandat,
                          dbname: dbName,
                        },
                      }).then(() => {
                        setReloadingAnalysList(true);
                        // Kan inte refetcha 2 st EkonomiskAnalysListQuery samtidigt, pga race-condition
                        return apolloClient
                          .query({
                            query: EkonomiskAnalysListQuery,
                            variables: {
                              codeSystemId,
                              onlyFavorites: false,
                              dbName: activeDatabase ?? '',
                            },
                            fetchPolicy: 'network-only',
                          })
                          .finally(() => setReloadingAnalysList(false));
                      })
                    }
                    initialUpdateStartAnalysAtLogin={
                      userInfo?.startAnalys?.rapportId ===
                      USE_NEW_AS_START_ANALYS_ID
                        ? false
                        : userInfo?.updateStartanalysAtLogin ?? true
                    }
                    {...props}
                  />
                </>
              )}
            </EkonomiskAnalysSaveDBSettingMutation>
          )}
        </ChangeStartAnalysMutation>
      )}
    </ChangeAnalysFavoriteMutation>
  );
};

export default EkonomiskAnalysFavoriteAnalysDialogButton;
